import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Wand2, Copy, Check, Loader2, Send, Plug } from "lucide-react";

function stripTitle(s) {
  if (!s) return "";
  const m = s.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
  return (m ? m[1] : s.replace(/<[^>]+>/g, "")).trim();
}
function stripMeta(s) {
  if (!s) return "";
  const m = s.match(/content=["']([\s\S]*?)["']/i);
  return (m ? m[1] : s.replace(/<[^>]+>/g, "")).trim();
}

function CodeBlock({ label, code, testid }) {
  const [copied, setCopied] = useState(false);
  if (!code) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success(`${label} copied`);
    } catch (e) {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="mb-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="uppercase-label">{label}</span>
        <button
          onClick={copy}
          className="flex items-center gap-1 text-xs font-semibold text-klein hover:underline"
          data-testid={`${testid}-copy`}
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="bg-[#0A0A0A] text-gray-100 text-xs rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-words font-mono leading-relaxed">
        {code}
      </pre>
    </div>
  );
}

export default function SeoFixes({ reportId }) {
  const [fixes, setFixes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const [conns, setConns] = useState([]);
  const [connId, setConnId] = useState("");
  const [content, setContent] = useState([]);
  const [itemKey, setItemKey] = useState("");
  const [pushing, setPushing] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/reports/${reportId}/fixes`);
      setFixes(data);
    } catch (e) {
      toast.error("Could not generate fixes", { description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const loadContent = async (id) => {
    setConnId(id);
    setContent([]);
    setItemKey("");
    if (!id) return;
    setLoadingContent(true);
    try {
      const { data } = await api.get(`/wordpress/connections/${id}/content`);
      setContent(data);
    } catch (e) {
      toast.error("Could not load WordPress content");
    } finally {
      setLoadingContent(false);
    }
  };

  const openPush = async () => {
    try {
      const { data } = await api.get("/wordpress/connections");
      if (!data || data.length === 0) {
        toast.error("No WordPress site connected", { description: "Connect one under the WordPress tab first." });
        return;
      }
      setConns(data);
      setPushOpen(true);
      loadContent(data[0].id);
    } catch (e) {
      toast.error("Could not load WordPress connections");
    }
  };

  const pushToWp = async () => {
    if (!connId || !itemKey) {
      toast.error("Pick a page or post to update");
      return;
    }
    const item = content.find((c) => `${c.type}-${c.id}` === itemKey);
    const alts = (fixes?.image_alts || []).filter((a) => a.src && a.alt);
    setPushing(true);
    try {
      const { data } = await api.post(`/wordpress/connections/${connId}/apply-all`, {
        object_type: item.type,
        object_id: item.id,
        title: stripTitle(fixes.title_tag) || undefined,
        meta_description: stripMeta(fixes.meta_description) || undefined,
        image_alts: alts,
      });
      const parts = [];
      if (data.title_applied) parts.push("title");
      if (data.meta_applied) parts.push("meta");
      if (data.images_updated) parts.push(`${data.images_updated} image alt${data.images_updated > 1 ? "s" : ""}`);
      const extra = data.images_unmatched ? ` · ${data.images_unmatched} image(s) not found in media` : "";
      toast.success("All fixes applied to WordPress", {
        description: (parts.length ? `Updated ${parts.join(", ")}` : "No changes") + extra,
      });
      setPushOpen(false);
    } catch (e) {
      toast.error("Could not push", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setPushing(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 animate-fade-up" data-testid="seo-fixes-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Wand2 className="w-5 h-5 text-klein" />
          <h3 className="font-heading text-xl font-bold tracking-tight">Ready-to-Apply Fixes</h3>
        </div>
        {!fixes && (
          <Button onClick={generate} disabled={loading} data-testid="generate-fixes-button" className="bg-[#0A0A0A] hover:bg-gray-800">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
            {loading ? "Generating…" : "Generate Fixes"}
          </Button>
        )}
      </div>

      {!fixes && !loading && (
        <p className="text-gray-500 text-sm mt-2">
          Generate copy-paste optimized meta tags, schema, robots.txt, sitemap & image alt text tailored to this page.
        </p>
      )}

      {loading && (
        <div className="flex items-center gap-3 text-gray-500 mt-4">
          <Loader2 className="w-5 h-5 animate-spin text-klein" />
          <span className="text-sm">Writing tailored SEO fixes…</span>
        </div>
      )}

      {fixes && (
        <div className="mt-5">
          {fixes.notes && (
            <div className="bg-gray-50 border-l-4 border-l-klein rounded-r-lg p-3 text-sm text-gray-700 mb-5">
              {fixes.notes}
            </div>
          )}
          <div className="flex justify-end mb-4">
            <Button onClick={openPush} data-testid="push-to-wordpress-button" className="bg-klein hover:bg-blue-800 text-white">
              <Send className="w-4 h-4 mr-2" /> Push to WordPress
            </Button>
          </div>
          <CodeBlock label="Title Tag" code={fixes.title_tag} testid="fix-title" />
          <CodeBlock label="Meta Description" code={fixes.meta_description} testid="fix-meta" />
          <CodeBlock label="H1 Heading" code={fixes.h1} testid="fix-h1" />
          <CodeBlock label="Open Graph & Twitter" code={fixes.og_tags} testid="fix-og" />
          <CodeBlock label="JSON-LD Structured Data" code={fixes.json_ld} testid="fix-jsonld" />
          <CodeBlock label="robots.txt" code={fixes.robots_txt} testid="fix-robots" />
          <CodeBlock label="sitemap.xml" code={fixes.sitemap_xml} testid="fix-sitemap" />

          {Array.isArray(fixes.image_alts) && fixes.image_alts.length > 0 && (
            <div className="mt-2" data-testid="fix-image-alts">
              <span className="uppercase-label">Image ALT Text</span>
              <div className="mt-2 space-y-2">
                {fixes.image_alts.map((im, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-3">
                    <div className="text-xs text-gray-400 truncate mb-1">{im.src}</div>
                    <div className="text-sm text-gray-800 font-medium">alt="{im.alt}"</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={pushOpen} onOpenChange={setPushOpen}>
        <DialogContent data-testid="push-wp-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Push fixes to WordPress</DialogTitle>
            <DialogDescription>
              Apply the AI-generated title & meta description to a live page or post.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {conns.length > 1 && (
              <div>
                <label className="uppercase-label">Site</label>
                <select
                  data-testid="push-wp-connection"
                  value={connId}
                  onChange={(e) => loadContent(e.target.value)}
                  className="mt-1.5 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  {conns.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} — {c.site_url}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="uppercase-label">Page / Post</label>
              {loadingContent ? (
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading content…
                </div>
              ) : (
                <select
                  data-testid="push-wp-item"
                  value={itemKey}
                  onChange={(e) => setItemKey(e.target.value)}
                  className="mt-1.5 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  <option value="">Select a page or post…</option>
                  {content.map((it) => (
                    <option key={`${it.type}-${it.id}`} value={`${it.type}-${it.id}`}>
                      [{it.type}] {it.title}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1.5">
              <div><span className="uppercase-label">New title</span><div className="text-gray-800">{stripTitle(fixes?.title_tag)}</div></div>
              <div><span className="uppercase-label">New meta</span><div className="text-gray-600 text-xs">{stripMeta(fixes?.meta_description)}</div></div>
              <div><span className="uppercase-label">Image alt text</span><div className="text-gray-600 text-xs" data-testid="push-wp-alt-count">{(fixes?.image_alts || []).filter((a) => a.src && a.alt).length} image(s) will be updated in your Media Library</div></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPushOpen(false)} className="border-gray-300">Cancel</Button>
            <Button onClick={pushToWp} disabled={pushing} data-testid="push-wp-submit" className="bg-[#0A0A0A] hover:bg-gray-800">
              {pushing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Apply all fixes</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
